import { determineBundleType, determineVersion, FoundationGlobal, UserConfig } from '@foundation-player/builder';
import type { LoaderConfig, PlayerInterface } from '@foundation-player/types';
import { loadPackages } from './load-packages';

type Window = FoundationGlobal & {
  foundation: {
    loader: {
      createPlayer: typeof createPlayer;
    };
  };
};

/**
 * Create the Foundation Player with a preset to get a fully instantiated and configured player including plugins.
 * All needed bundles are loaded from the foundation player CDN (cdn.player.foundation) at runtime.
 * After loading the necessary scripts the player is instantiated and returned.
 *
 * Documentation: https://docs.player.tvnow.de
 **/
export async function createPlayer(loaderConfig: LoaderConfig, loaderStartTime?: number): Promise<PlayerInterface> {
  // TODO: check if we can do this in a better way
  // We want to make sure clients always use the LoaderConfig type but internally we want to use the UserConfig type
  // We do this in order to not expose the UserConfig type to the public, but still be able to enable stuff for debugging
  const userConfig = loaderConfig as UserConfig;

  const isNPM = !(window as unknown as Partial<Window>).foundation?.loader;
  const loaderStart = loaderStartTime ?? performance.now();
  const [version, overwrite] = determineVersion(userConfig.version);
  const bundleType = determineBundleType(userConfig.bundleType);
  const useLocalBundles = overwrite ? false : userConfig.isLocal;
  await loadPackages({
    version,
    bundleType,
    isLocal: overwrite ? false : userConfig.isLocal,
    cdn: overwrite ? undefined : userConfig.cdn,
  });

  const foundationGlobal = (window as unknown as Window).foundation;
  if (isNPM) {
    // if the player was included via npm we have to use the newly loaded Loader instead
    // to avoid conflicts with different versions of the npm package and the actual runtime version
    return foundationGlobal.loader.createPlayer(userConfig, loaderStart);
  }

  const builder = new foundationGlobal.builder.Builder();
  const player = await builder.build({
    ...userConfig,
    bundleType,
    isLocal: Boolean(useLocalBundles),
  });

  const loaderEnd = performance.now();
  const coreBundle = foundationGlobal.player.core;
  player.dispatchEvent(
    new coreBundle.MetricEvent(coreBundle.PlayerMetric.LoaderCreation, {
      start: loaderStart,
      end: loaderEnd,
    }),
  );
  return player;
}
